import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ChevronRight, Users } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';

interface TopCitiesLeaderboardProps {
  className?: string;
}

const TopCitiesLeaderboard: React.FC<TopCitiesLeaderboardProps> = ({ className }) => {
  const [cities, setCities] = useState<{ city: string; count: number }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTopCities = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('user_counts_by_city')
          .select('city, count')
          .order('count', { ascending: false })
          .limit(5);

        if (error) throw error;
        setCities(data || []);
      } catch (error) {
        console.error('Error fetching top cities:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTopCities();
  }, []);

  const handleCityClick = (city: string) => {
    navigate('/dashboard/facilities', {
      state: { selectedCity: city }
    });
  };

  const handleViewAllClick = () => {
    navigate('/dashboard/facilities');
  };

  if (isLoading) {
    return (
      <div className={cn("bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6 animate-pulse", className)}>
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 w-40 bg-dark-300 rounded"></div>
          <div className="h-6 w-24 bg-dark-300 rounded"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-8 w-8 bg-dark-300 rounded-full mr-3"></div>
                <div className="h-4 w-32 bg-dark-300 rounded"></div>
              </div>
              <div className="h-4 w-16 bg-dark-300 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (cities.length === 0) return null;

  return (
    <div className={cn("bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6", className)}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <MapPin className="h-5 w-5 text-gold-400 mr-2" />
          <h3 className="text-lg font-semibold text-white">Top Cities</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleViewAllClick}
          className="text-dark-800 hover:text-white"
        >
          View All
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
      
      <div className="space-y-3">
        {cities.map((city, index) => (
          <button
            key={city.city}
            onClick={() => handleCityClick(city.city)}
            className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-dark-300 transition-colors"
          >
            <div className="flex items-center">
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center mr-3",
                index < 3 ? 
                  index === 0 ? "bg-gold-400/20 text-gold-400" : 
                  index === 1 ? "bg-silver-400/20 text-silver-400" : 
                  "bg-bronze-400/20 text-bronze-400"
                : "bg-dark-300 text-dark-800"
              )}>
                {index + 1}
              </div>
              <div className="flex items-center">
                <MapPin className="h-4 w-4 text-dark-800 mr-1" />
                <span className="text-white">{city.city}</span>
              </div>
            </div>
            <div className="flex items-center text-dark-800">
              <Users className="h-4 w-4 mr-1" />
              <span>{city.count}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default TopCitiesLeaderboard;